/**
 * A page of `/people` turned into the two lists the unit frame's VISITAS and PREST. tabs render.
 *
 * Extracted from LocationDetailView's fetch effect for one reason: the reported bug was HERE, and
 * nothing could reach it. The effect decided which tab a role belongs to by `role.role_type` while
 * `unitLiberadosRows` — tested, green — decided the same thing by `role_metadata.category`. A
 * prestador registered as a `visitor` role (which is how every prestador liberação is written) had
 * its visits on one tab and its clearance on the other, so unit 188 listed four people directly
 * under the words "LIBERADOS 0 · Nenhum visitante liberado".
 *
 * Both splits go through `isPrestadorVisit` now, and this file exists so that can be asserted.
 */
import { LiberacaoSummary, summarizeLiberacoes } from '../../../utils/liberacoes';
import { isPrestadorVisit } from './unitLiberadosRows';

export interface VisitRow {
  id: string;
  personId: string;
  name: string;
  photo: string | null;
  state: string;
  createdAt: string;
  firstEntryAt?: string;
  lastEntryAt?: string;
  lastExitAt?: string;
  authorizedByName?: string;
  /** Set when this visit is a liberação (período/grade/sempre habilitado) — see utils/liberacoes. */
  liberacao?: LiberacaoSummary;
}

export interface PrestadorRow extends VisitRow {
  windowStart?: string;
  windowEnd?: string;
  revokedAt?: string;
  updatedAt?: string;
}

export interface UnitVisitRows {
  /** The VISITAS tab. */
  visits: VisitRow[];
  /** The PREST. tab. */
  spVisits: PrestadorRow[];
}

export interface LocationScope {
  locationType: string;
  locationId: string;
}

/**
 * Does this role belong to the location on screen?
 *
 * A role that records NO location is kept: legacy rows carry only `added_by`, and dropping them
 * hides a clearance from the one screen that can revoke it. The server makes the same choice
 * (liberacaoUnitScopeSQL).
 *
 * For an empresa the departamento is in `department_id`, or in `unit_id` on rows written before
 * that field existed.
 */
function belongsToLocation(meta: any, { locationType, locationId }: LocationScope): boolean {
  if (locationType === 'department') {
    const deptId = meta.department_id || meta.unit_id;
    return !deptId || String(deptId) === String(locationId);
  }
  if (locationType === 'unit') {
    return !meta.unit_id || String(meta.unit_id) === String(locationId);
  }
  return true;
}

/** Newest activity first — an entry, failing that an exit, failing that when it was created. */
const latestDate = (r: VisitRow): number =>
  new Date(r.lastEntryAt || r.lastExitAt || r.createdAt || 0).getTime();

export function splitUnitVisitRows(people: any[] | null | undefined, scope: LocationScope): UnitVisitRows {
  const visits: VisitRow[] = [];
  const spVisits: PrestadorRow[] = [];
  if (!Array.isArray(people)) return { visits, spVisits };

  for (const person of people) {
    if (!person) continue;
    const name = `${person.first_name || ''} ${person.last_name || ''}`.trim() || 'Visitante';
    const photo = person.identity_photo_url || person.profile_picture_url || null;

    for (const role of person.roles || []) {
      if (!role) continue;
      if (role.role_type !== 'visitor' && role.role_type !== 'service_provider') continue;
      const meta = role.role_metadata || {};
      if (!belongsToLocation(meta, scope)) continue;

      const row: VisitRow = {
        id: role.id,
        personId: person.id,
        name,
        photo,
        state: meta.visitor_state || 'unknown',
        createdAt: role.created_at,
        firstEntryAt: meta.first_entry_at,
        lastEntryAt: meta.last_entry_at,
        lastExitAt: meta.last_exit_at,
        authorizedByName: meta.authorized_by_name,
        liberacao: summarizeLiberacoes([role])[0],
      };

      // The tab is decided by isPrestadorVisit, NOT by the role type — see the header above.
      if (isPrestadorVisit(role)) {
        spVisits.push({
          ...row,
          windowStart: meta.entry_window_start,
          windowEnd: meta.entry_window_end,
          revokedAt: meta.revoked_at,
          updatedAt: role.updated_at,
        });
      } else {
        visits.push(row);
      }
    }
  }

  visits.sort((a, b) => latestDate(b) - latestDate(a));
  spVisits.sort((a, b) => latestDate(b) - latestDate(a));
  return { visits, spVisits };
}
